import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';

const ServicesShowcase: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const controls = useAnimation();

  const services = [
    {
      name: 'Individual Therapy',
      path: '/services/individual-therapy',
      description: 'Personal growth and healing in a safe, one-on-one environment',
      details: 'Our individual therapy sessions provide a confidential space for self-discovery and emotional healing.',
      image: '/images/individual-therapy2.jpg'
    },
    {
      name: 'Couple Therapy',
      path: '/services/couples-therapy',
      description: 'Strengthening bonds through effective communication',
      details: 'Build deeper connections and navigate relationship challenges together.',
      image: '/images/relationship2.jpg'
    },
    {
      name: 'Family Therapy',
      path: '/services/family-therapy',
      description: 'Healing together in a supportive community',
      details: 'Share experiences and grow alongside others in a guided, therapeutic environment.',
      image: '/images/group-therapy-2.jpg'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % services.length);
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
    // eslint-disable-next-line
  }, [activeIndex]);

  return (
    <section className="relative min-h-screen py-20 bg-gradient-to-br from-teal-900/95 to-teal-800/95">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Our Therapeutic Services
          </h2>
          <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full" />
        </motion.div>

        <div className="relative">
          {/* Service Cards */}
          <div className="relative h-[600px] overflow-hidden">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 100 }}
                animate={{
                  opacity: activeIndex === index ? 1 : 0,
                  y: activeIndex === index ? 0 : 100,
                  zIndex: activeIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="absolute inset-0"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 h-full">
                  {/* Image Side */}
                  <div className="relative overflow-hidden rounded-2xl">
                    <motion.img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover"
                      initial={{ scale: 1.1 }}
                      animate={{ scale: activeIndex === index ? 1 : 1.1 }}
                      transition={{ duration: 1.2 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-900/50 to-transparent" />
                  </div>

                  {/* Content Side */}
                  <div className="flex flex-col justify-center">
                    <motion.div
                      className="p-8 rounded-2xl bg-white/10 backdrop-blur-lg border border-white/20"
                      initial={{ x: 100, opacity: 0 }}
                      animate={{ 
                        x: activeIndex === index ? 0 : 100,
                        opacity: activeIndex === index ? 1 : 0
                      }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <h3 className="text-3xl font-bold text-white mb-4">
                        {service.name}
                      </h3>
                      <p className="text-xl text-white/80 mb-6">
                        {service.description}
                      </p>
                      <p className="text-white/70 mb-8">
                        {service.details}
                      </p>
                      <a
                        href={service.path}
                        className="inline-block px-6 py-3 bg-orange-400 text-white rounded-lg font-medium hover:bg-orange-500 transition-colors"
                      >
                        Learn More
                      </a>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center mt-8 space-x-3">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  activeIndex === index ? 'bg-orange-400' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesShowcase;